import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Grid, Box } from "@material-ui/core";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppInput from "components/Input/AppInput";
import SelectField from "components/Select/SelectField";
import httpRequest from "services/httpRequest";
import { showToast } from "store/slices/toast";
import AppColor from "utilities/AppColor";
import { YupValidations } from "utils/validate";

const SendIntakeLinkModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(t("validation.required")),
    last_name: Yup.string().required(t("validation.required")),
    recipient_email: YupValidations.requiredString.email("validation.email.invalid"),
    recipient_type: Yup.string().required(t("validation.required")),
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await httpRequest({
        url: "/client/intake-link/send",
        method: "POST",
        data: values,
      });
      if (response) {
        dispatch(showToast({ message: "Intake link sent successfully", type: "success" }));
        onSuccess && onSuccess();
        onClose();
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const recipientTypeOptions = [
    { value: "PARTICIPANT", label: "Participant" },
    { value: "NOMINEE", label: "Nominee" },
    { value: "FAMILY_MEMBER_OR_GUARDIAN", label: "Family Member or Guardian" },
    { value: "SUPPORT_COORDINATOR", label: "Support Coordinator" },
  ];

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Send Intake Link"
      maxWidth="sm"
    >
      <Formik
        initialValues={{
          first_name: "",
          last_name: "",
          recipient_email: "",
          recipient_type: "PARTICIPANT",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid }) => (
          <Form style={{ padding: "20px" }}>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="first_name"
                  label="First Name"
                  required
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="last_name"
                  label="Last Name"
                  required
                />
              </div>
            </div>
            
            <div className="row">
              <div className="col-sm-12 col-md-12">
                <Field
                  component={AppInput}
                  name="recipient_email"
                  label="Recipient Email"
                  required
                />
              </div>
            </div>
            
            <div className="row">
              <div className="col-sm-12 col-md-12">
                <Field
                  component={SelectField}
                  label="Recipient Type"
                  name="recipient_type"
                  options={recipientTypeOptions}
                  required
                />
              </div>
            </div>

            <Box mt={3} display="flex" justifyContent="flex-end">
              <AppButton
                label="Cancel"
                buttonColor="#9ca3af"
                onClick={onClose}
                disabled={loading}
                style={{ marginRight: 10 }}
              />
              <AppButton
                label="Send Link"
                type="submit"
                buttonColor={AppColor.mainColor}
                isLoading={loading}
                disabled={!isValid || loading}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </AppDialog>
  );
};

SendIntakeLinkModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default SendIntakeLinkModal;
